import type { Session } from '@orgus/protocol';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Sparkline, StatStrip, StatTile, StateBar, formatCompact, formatDuration, formatInt, formatMoney, plural, useNow, cn } from '@orgus/ui';
import { useFeed } from '../../lib/store.ts';
import { useCosts } from '../costs/useCosts.ts';
import { spendSummary } from '../costs/model.ts';
import { liveSessionCounts } from '../overview/counts.ts';
import { agentStarts } from '../overview/pulse.ts';
import { blockedSince, oldestWait, runningAgents } from './groups.ts';
import { tokenTotal } from './labels.ts';
import { useSessionFilters } from './filters.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { blockedOnly } from '../asks/levels.ts';

/** Days of spend in the spend tile's trend. */
const SPEND_DAYS = 14;

/**
 * The sessions glance strip: four numbers on their own surfaces, Needs you first. Each tile
 * narrows the list below or opens the page that holds its number. Every figure reads the same
 * scope as its number; a tile that cannot follow the scope (spend) says so (`narrowed`).
 */
export function SessionsGlance({ sessions, narrowed = false, className }: { sessions: readonly Session[]; narrowed?: boolean; className?: string }) {
  const navigate = useNavigate();
  const [, set] = useSessionFilters();
  const costs = useCosts();
  const feed = useFeed(500);
  const now = useNow(30_000);
  // The one selector (ADR 0056): blocked sessions are amber, asked ones count quietly, old or dismissed ones not at all.
  const needs = useNeedsYouState();
  const levels = needs.bySession;
  const need = useMemo(() => {
    let blocked = 0;
    let asked = 0;
    for (const s of sessions) {
      if (s.status !== 'waiting') continue;
      const l = levels.get(s.id);
      if (l === 'asked') asked++;
      else if (l === 'blocked') blocked++;
    }
    return { blocked, asked, total: blocked + asked };
  }, [sessions, levels]);
  // Waiting means blocked in the live counts too (ADR 0056).
  const live = useMemo(() => liveSessionCounts(blockedOnly(sessions, levels)), [sessions, levels]);
  // The same blocked-only reading as the Needs you group header and Overview: what blocks, from when its ask began.
  const wait = useMemo(() => oldestWait(blockedOnly(sessions, levels), now, blockedSince(needs.blocked)), [sessions, levels, now, needs.blocked]);
  const agents = useMemo(() => runningAgents(sessions), [sessions]);
  const starts = useMemo(() => agentStarts(feed, now), [feed, now]);
  const tokens = useMemo(() => sessions.reduce((n, s) => (s.status === 'ended' ? n : n + tokenTotal(s)), 0), [sessions]);
  // The same figures the Spend hero shows (one definition, QA-1).
  const sum = useMemo(() => (costs ? spendSummary(costs) : null), [costs]);
  const spend = useMemo(() => (costs?.byDay ?? []).slice(-SPEND_DAYS).map((d) => d.cost), [costs]);

  return (
    <StatStrip label="Session counts" className={cn('lg:grid-cols-4', className)}>
      <StatTile
        label="Needs you"
        value={need.total}
        attention={need.blocked > 0}
        sub={need.blocked ? (wait ? `Oldest waiting ${formatDuration(wait)}` : 'Waiting under 1m') : need.asked ? `${formatInt(need.asked)} asked` : 'Nothing needs you'}
        // No bar at zero (an empty track reads broken); asked questions draw a quiet share of their own.
        visual={need.total > 0 ? <StateBar counts={{ 'needs-you': need.blocked, queued: need.asked, stale: Math.max(0, live.live - need.blocked - need.asked) }} label={`${formatInt(need.blocked)} blocked, ${formatInt(need.asked)} asked, of ${plural(live.live, 'live session')}`} /> : undefined}
        onOpen={() => set({ status: 'waiting' })}
      />
      <StatTile
        label="Live sessions"
        value={live.live}
        sub={[live.waiting && `${formatInt(live.waiting)} need you`, live.working && `${formatInt(live.working)} working`, live.idle && `${formatInt(live.idle)} idle`].filter(Boolean).join(', ') || 'None live'}
        visual={live.live > 0 ? <StateBar counts={{ 'needs-you': live.waiting, running: live.working, stale: live.idle }} label={`${formatInt(live.waiting)} need you, ${formatInt(live.working)} working, ${formatInt(live.idle)} idle`} /> : undefined}
        onOpen={() => set({ status: 'live' })}
      />
      <StatTile
        label="Subagents running"
        featured
        value={agents.subagents}
        sub={agents.workflows ? `${plural(agents.workflows, 'workflow')} running` : 'No workflow running'}
        // The starts trend is fleet wide: shown beside a count it can explain, never a spike beside 0.
        trend={agents.subagents > 0 ? <Sparkline values={starts} width={88} height={32} min={0} slot={1} label={`Agent starts in the last hour: ${formatInt(starts.reduce((a, n) => a + n, 0))}`} /> : undefined}
        onOpen={() => navigate('/agents')}
      />
      {costs && sum ? (
        <StatTile
          label="Spend today"
          value={sum.today}
          format={formatMoney}
          // Spend is not kept per filter: under a search or a scope the tile says it counts everything.
          // While transcripts are still being read the figure climbs: the tile says so, as Spend does.
          sub={narrowed ? 'All sessions' : sum.reading ? `Still reading ${plural(sum.reading, 'session')}` : `${formatMoney(sum.month)} this month`}
          trend={spend.length > 1 ? <Sparkline values={spend} width={88} height={32} min={0} slot={3} label={`Spend over the last ${formatInt(spend.length)} days`} /> : undefined}
          onOpen={() => navigate('/spend')}
        />
      ) : (
        // Without cost tracking there are no dollars to show: the tile says what it holds instead.
        <StatTile label="Tokens in live sessions" value={tokens} format={formatCompact} sub="Turn on cost tracking for dollars" onOpen={() => navigate('/spend')} />
      )}
    </StatStrip>
  );
}
